import { Injectable } from '@angular/core';
import { Observable, map, of, catchError } from 'rxjs';
import { AppConstants } from '../../../app.constants';
import { GenericResponseModel } from '../../models/genericResponse.model';
import { SupplierCreateRequestModel } from '../../models/supplierDB/supplierCreateRequest.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ToastHandlerService } from '../other/tostHandler.service';
import { ToastMessageSeverity } from '../../enums/toastMessageTypes.enum';
import { L10nTranslationService } from 'angular-l10n';
import { SupplierContactFormModel } from '../../models/supplierDB/supplierContactForm.model';

@Injectable()
export class HttpSupplierDbService {
  constructor(
    private http: HttpClient,
    private toastService: ToastHandlerService,
    private translationService: L10nTranslationService
  ) {}

  /**
   * Registers a new supplier in the SupplierDB.
   * @param info - The information required to create a new supplier.
   * @returns {number | string} - The ID of the newly created supplier if successful
   *
   * The function sends a PUT request to the specified endpoint with the provided supplier information.
   * It handles the response by displaying appropriate toast messages based on the result.
   * If an error occurs during the request, it displays a general error message and returns false.
   */
  public registerNewSupplier(
    info: SupplierCreateRequestModel
  ): Observable<null | string> {
    var token = localStorage.getItem(AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_ACCESS_TOKEN);
    let options = {};
    if (token != null) {
      options = { params: new HttpParams().append("token", token) };
    }
    return this.http
      .put<GenericResponseModel>(AppConstants.SDB_SUPPLIER_REGISTER, info, options)
      .pipe(
        map((response) => {
          if (response.result === false) {
            this.toastService.showMessage(
              ToastMessageSeverity.error,
              this.translationService.translate('CARRIERDB.EXCEPTION.GENERAL'),
              this.translationService.translate(response.message)
            );
            return null;
          } else {
            this.toastService.showMessage(
              ToastMessageSeverity.success,
              this.translationService.translate('GENERAL.SUCCESS'),
              this.translationService.translate('CARRIERDB.SUCCESS_MESSAGE')
            );
          }
          return response.result;
        }),
        catchError(() => {
          this.toastService.showMessage(
            ToastMessageSeverity.error,
            this.translationService.translate('CARRIERDB.EXCEPTION.GENERAL'),
            this.translationService.translate(
              'CARRIERDB.EXCEPTION.MESSAGE_SUPPORT'
            )
          );
          return of(null);
        })
      );
  }

  /**
   * Loads staged supplier data based on the provided access token
   * @param {string} token the provided access token for the public endpoint
   * @returns {null | Partial<SupplierContactFormModel>}
   */
  public loadDataByToken(token: string): Observable<null | Partial<SupplierContactFormModel>> {
    const params = new HttpParams().set("token", token);
    return this.http
      .get<GenericResponseModel>(AppConstants.SDB_SUPPLIER_GET_DATA_BY_TOKEN, { params })
      .pipe(
        map((response) => {
          if (!response.result) {
            this.toastService.showMessage(
              ToastMessageSeverity.error,
              this.translationService.translate('CARRIERDB.EXCEPTION.GENERAL'),
              this.translationService.translate(response.message)
            );
            return null;
          }
          return response.result;
        }),
        catchError(() => {
          this.toastService.showMessage(
            ToastMessageSeverity.error,
            this.translationService.translate('CARRIERDB.EXCEPTION.GENERAL'),
            this.translationService.translate(
              'CARRIERDB.EXCEPTION.MESSAGE_SUPPORT'
            )
          );
          return of(null);
        })
      );
  }
}
